import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";
import { peoplePerPage } from "../../utils/perPageValue";

// 🔹 Async Thunk: Start session
export const startSession = createAsyncThunk(
  "session/startSession",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.startSession, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// 🔹 Async Thunk: Parse Intent
export const parseIntent = createAsyncThunk(
  "session/parseIntent",
  async (
    { user_id, session_id, prompt, use_goapi = true, website_url },
    { rejectWithValue }
  ) => {
    try {
      const payload = { user_id, session_id, prompt, use_goapi, website_url };

      const response = await axios.post(apiEndpoints.parseIntent, payload, {
        headers: { "Content-Type": "application/json" },
      });

      return response.data;
    } catch (error) {
      console.error(
        "❌ parseIntent API error:",
        error.response || error.message
      );
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// 🔹 Async Thunk: Search Companies
export const searchCompanies = createAsyncThunk(
  "session/searchCompanies",
  async (
    {
      prompt,
      industries = [],
      // new: also accept canonical keywords and locations
      keywords = "",
      keywords1 = "",
      locations = [],
      companies = {
        organization_num_employees_ranges: [
          "11-50",
          "51-200",
          "201-500",
          "501-1000",
        ],
      },
      technologies = [],
      ipo_status = "public",
      sort_by_field = "organization_created_at",
      sort_ascending = true,
      page = 1,
      per_page = 50,
    },
    { rejectWithValue }
  ) => {
    try {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        return rejectWithValue({ message: "No session found in localStorage" });
      }

      // Normalize and map: if industries is empty, use technologies; if still empty, use keywords/keywords1
      const toArray = (v) => (Array.isArray(v) ? v : []);
      const clean = (arr) =>
        toArray(arr)
          .map((x) => (x == null ? "" : String(x).trim()))
          .filter(Boolean);

      const normIndustries = clean(industries);
      const normTech = clean(technologies);
      const finalIndustries =
        normIndustries.length > 0
          ? normIndustries
          : normTech.length > 0
          ? normTech
          : (typeof keywords === "string" && keywords.trim()) ||
            (typeof keywords1 === "string" && keywords1.trim())
          ? [String(keywords || keywords1).trim()]
          : [];

      // normalize locations
      const finalLocations = clean(locations);

      // canonical keywords string
      const finalKeywords =
        typeof keywords === "string" && keywords.trim()
          ? keywords.trim()
          : typeof keywords1 === "string" && keywords1.trim()
          ? keywords1.trim()
          : finalIndustries[0] || "";

      const payload = {
        session_id: sessionId,
        prompt,
        intent: {
          audience: "companies",
          industries: finalIndustries,
          locations: finalLocations,
          keywords: finalKeywords,
        },
        // keep root-level mirrors for backend compatibility
        keywords: finalKeywords,
        keywords1, // legacy support
        companies,
        page,
        per_page,
        technologies,
        ipo_status,
        sort_by_field,
        sort_ascending,
      };

      const response = await axios.post(apiEndpoints.searchCompanies, payload, {
        headers: { "Content-Type": "application/json" },
      });

      return response.data;
    } catch (error) {
      console.error("❌ searchCompanies error:", error.message);
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// new api search
export const searchPeople = createAsyncThunk(
  "session/searchPeople",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.searchPeople, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error(
        "❌ parseIntent API error:",
        error.response || error.message
      );
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// new api search
export const seachApi = createAsyncThunk(
  "session/seachApi",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.searchApi, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error(
        "❌ parseIntent API error:",
        error.response || error.message
      );
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);


export const searchPeopleByOrg = createAsyncThunk(
  "session/searchPeopleByOrg",
  async (
    { orgId, page = 1, per_page = peoplePerPage },
    { rejectWithValue }
  ) => {
    try {
      if (!orgId) {
        return rejectWithValue({ message: "organization id is required" });
      }

      // ✅ Use function form of api endpoint
      const url = `${apiEndpoints.getorgidpeoples(
        orgId
      )}?page=${page}&per_page=${per_page}`;

      const response = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
      });

      return response.data;
    } catch (error) {
      console.error("❌ searchPeopleByOrg error:", error.response || error);
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// 🔹 Async Thunk: Suggest API
export const fetchSuggestions = createAsyncThunk(
  "session/fetchSuggestions",
  async ({ q, max_per_bucket = 8 }, { rejectWithValue }) => {
    try {
      // ✅ Try to get from localStorage, fallback to null
      const sessionId = localStorage.getItem("sessionId") || null;

      const payload = {
        q,
        session_id: sessionId,
        max_per_bucket,
      };

      const response = await axios.post(
        apiEndpoints.suggestionPrompt,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

const appoloNewSlice = createSlice({
  name: "session",
  initialState: {
    sessionData: null,
    parseIntentData: null,
    companyResults: null,
    peopleResults: null,
    suggestions: null,
    loading: false,
    error: null,
    searchAPiData: null,
  },
  reducers: {
    resetSession: (state) => {
      state.sessionData = null;
      state.parseIntentData = null;
      state.companyResults = null;
      state.peopleResults = null;
      state.error = null;

      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Start Session
      .addCase(startSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionData = action.payload;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Parse Intent
      .addCase(parseIntent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(parseIntent.fulfilled, (state, action) => {
        state.loading = false;
        state.parseIntentData = action.payload;
      })
      .addCase(parseIntent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search Companies
      .addCase(searchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companyResults = action.payload;
      })
      .addCase(searchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search People (payload-based)
      .addCase(searchPeople.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPeople.fulfilled, (state, action) => {
        state.loading = false;
        state.peopleResults = action.payload;
      })
      .addCase(searchPeople.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search People by Org (GET)
      .addCase(searchPeopleByOrg.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPeopleByOrg.fulfilled, (state, action) => {
        state.loading = false;
        state.peopleResults = action.payload;
      })
      .addCase(searchPeopleByOrg.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Suggestions
      .addCase(fetchSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload; // 👈 Add new field
      })
      .addCase(fetchSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(seachApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(seachApi.fulfilled, (state, action) => {
        state.loading = false;
        state.searchAPiData = action.payload; // 👈 Add new field
      })
      .addCase(seachApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSession } = appoloNewSlice.actions;
export default appoloNewSlice.reducer;
